import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatusPill from "@/components/StatusPill";
import ServiceNowPanel from "@/components/ServiceNowPanel";
import { formatDate, displayReqNumber } from "@/lib/format";
import { ExternalLink } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "Pending Approval", label: "Pending Approval" },
  { id: "Submitted", label: "Submitted" },
  { id: "In Progress", label: "In Progress" },
  { id: "Approved", label: "Approved" },
  { id: "Fulfilled", label: "Fulfilled" },
];

// Hardcoded "featured" ticket used to seed the ITSM view for leadership demos.
// Rendered at position 3 (or last if fewer rows) with a light-yellow highlight.
const HIGHLIGHT_TICKET = {
  id: "highlight-req-0010042",
  req_number: "REQ0010042",
  sn_number: "REQ0010042",
  sx_number: "ITM000000515952",
  sn_table: "sc_request",
  sn_sys_id: "highlight-sys-id-0010042",
  subject: "Device Refresh · Bndl ZF ZB FuryG1i16 ST EXPR w ProvCon · Executive briefing kit",
  location: "USA - NRT",
  priority: "High",
  assigned_group: "Endpoint Services · Executive Support",
  category: "Software Request",
  status: "Fulfilled",
  created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  __highlight: true,
};

// kind: "all" (REQs + incidents) | "incidents" (incident table only)
export default function Tickets({ embedded = false, kind = "all" }) {
  const { persona, userEmail } = usePersona();
  const [rawTickets, setTickets] = useState([]);
  const tickets = kind === "incidents" ? rawTickets.filter((t) => t.sn_table === "incident") : rawTickets;
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [snOpen, setSnOpen] = useState(false);
  const [snTarget, setSnTarget] = useState({ sysId: "", table: "sc_request", number: "", fallbackReq: "" });

  const openSnPanel = (t) => {
    setSnTarget({
      sysId: t.sn_sys_id || "",
      table: t.sn_table || (t.category === "Software Request" ? "sc_request" : "incident"),
      number: t.sn_number || "",
      fallbackReq: t.req_number || "",
    });
    setSnOpen(true);
  };

  useEffect(() => {
    setLoading(true);
    const params = { persona: persona.id, status };
    if (userEmail) params.user_email = userEmail;
    endpoints.tickets(params).then((d) => { setTickets(d); setLoading(false); }).catch(() => setLoading(false));
  }, [persona.id, status, userEmail]);

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className={`flex items-start flex-wrap gap-3 ${embedded ? "justify-end" : "justify-between"}`}>
        {!embedded && (
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">ITSM</div>
            <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">Open Tickets</h1>
            <p className="text-sm text-slate-600 mt-1">All ServiceNow requests raised by you through Flexspace.</p>
          </div>
        )}
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="bg-slate-100 h-auto flex-wrap justify-start" data-testid="tickets-filter">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.id} value={f.id} data-testid={`ticket-filter-${f.id}`} className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <Card className="p-0 border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="tickets-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">REQ #</th>
              <th className="text-left px-4 py-3 font-semibold">Subject</th>
              <th className="text-left px-4 py-3 font-semibold">Priority</th>
              <th className="text-left px-4 py-3 font-semibold">Assigned To</th>
              <th className="text-left px-4 py-3 font-semibold">Created</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">ServiceNow</th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-t border-slate-200"><td colSpan={7} className="p-3"><Skeleton className="h-6 w-full" /></td></tr>
            ))}
            {!loading && tickets.length === 0 && kind === "incidents" && (
              <tr><td colSpan={7} className="p-10 text-center text-slate-500">{kind === "incidents" ? "No incidents raised. Use Report Issue at the top to log one." : "No tickets in this view."}</td></tr>
            )}
            {(() => {
              // Splice the highlighted demo row in at position 3 (0-indexed 2). If fewer rows, append it.
              const rows = [...tickets];
              if (kind === "incidents") return rows;
              const idx = Math.min(2, rows.length);
              rows.splice(idx, 0, HIGHLIGHT_TICKET);
              return rows;
            })().map((t) => {
              const displayNum = displayReqNumber(t);
              const highlight = t.__highlight === true;
              return (
              <tr
                key={t.id}
                data-testid={highlight ? "tickets-highlight-row" : undefined}
                className={`border-t border-slate-200 ${highlight ? "bg-[#FEF9C3]/70 hover:bg-[#FEF9C3]" : "hover:bg-slate-50"}`}
              >
                <td className="px-4 py-3">
                  <Link to={highlight ? "#" : `/tickets/${t.id}`} data-testid={`open-ticket-${displayNum}`} className="font-mono text-xs font-semibold text-[#0033A0] hover:underline">
                    {displayNum}
                  </Link>
                  {highlight && t.sx_number && (
                    <div
                      data-testid="tickets-highlight-sx-number"
                      className="text-[10px] text-slate-600 font-mono mt-0.5"
                      title="SX Ticket"
                    >
                      SX # {t.sx_number}
                    </div>
                  )}
                  {t.sn_number && t.sn_number !== t.req_number && (
                    <div className="text-[9px] text-slate-400 font-mono mt-0.5" title="Local Flexspace REQ">fx {t.req_number}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-900">
                  {highlight ? (
                    <span className="hover:underline cursor-default">{t.subject}</span>
                  ) : (
                    <Link to={`/tickets/${t.id}`} className="hover:underline">{t.subject}</Link>
                  )}
                  {highlight && t.location && (
                    <div
                      data-testid="tickets-highlight-location"
                      className="text-[10px] text-slate-600 font-mono mt-0.5"
                    >
                      Location # {t.location}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold ${t.priority === "Critical" ? "text-[#DA291C]" : t.priority === "High" ? "text-amber-600" : "text-slate-600"}`}>{t.priority}</span>
                </td>
                <td className="px-4 py-3 text-slate-600">{t.assigned_group}</td>
                <td className="px-4 py-3 text-slate-600">{formatDate(t.created_at)}</td>
                <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`open-sn-ticket-${displayNum}`}
                    onClick={() => openSnPanel(t)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono border transition-colors ${
                      t.sn_sys_id
                        ? "bg-[#293e40] text-white border-[#293e40] hover:bg-[#1a2828]"
                        : "bg-slate-50 text-slate-600 border-slate-300 hover:bg-slate-100"
                    }`}
                    title={t.sn_sys_id ? "View live ServiceNow record" : "Preview — connect a PDI to see live record"}
                  >
                    {t.sn_number || (t.sn_sys_id ? "Open" : "Preview")}
                    <ExternalLink size={10} />
                  </button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </Card>

      <ServiceNowPanel
        open={snOpen}
        onOpenChange={setSnOpen}
        table={snTarget.table}
        sysId={snTarget.sysId}
        number={snTarget.number}
        fallbackReq={snTarget.fallbackReq}
      />
    </div>
  );
}
